import React, { useRef } from 'react'

import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react'

import { AppMode } from '@customTypes/Generic'
import { PatientTypeOptions } from 'src/config/patientTypeOptions'
import Spinner from '@components/Spinner'
import { useAppointmentCalendar } from '@context/AppointmentCalendarContext/AppointmentCalendarContext'
import { useChoiceState } from '@context/ChoiceContext'
import { useConfig } from '@context/ConfigurationContext'
import { useResetPreferredPractitionersInMultipleCLinics } from '@hooks/useResetPreferredPractitionersInMultipleCLinics'
import { useScrollIntoView } from '@hooks/useScrollIntoView'
import { useCalendarVariant } from '@screens/Appointment/helpers/useCalendarVariant'
import { ClinicSlots } from '../ClinicSlots/ClinicSlots'
import { ClinicSlotsInfo } from '../ClinicSlotsInfo/ClinicSlotsInfo'
import { ToggleMoreClinicsButton } from '../ToggleMoreClinicsButton/ToggleMoreClinicsButton'
import logUserInteraction from 'src/react/screens/Appointment/helpers/logUserInteraction'
import { CALENDAR_DIRECTIONS } from '@customTypes/AppointmentCalendarDirections'
import { CALENDAR_USER_ACTIONS } from '@customTypes/AppointmentCalendarUserActions'

export const MultipleClinics = () => {
  const { hasMoreClinicsInCity, currentPeriod, cityClinicsLoading, isTransitioning, swipeToPeriod } =
    useAppointmentCalendar()

  const {
    selectedBookableDateAndTime,
    patientType,
    allPreferredPractitionersAreSelected,
    preferredPractitionerReferenceIds,
  } = useChoiceState()

  useResetPreferredPractitionersInMultipleCLinics()

  const { mode } = useConfig()
  const isNormalMode = mode === AppMode.NORMAL

  const activeSlotRef = useRef<HTMLDivElement | null>(null)
  const toggleMoreClinicsButtonRef = useRef<HTMLDivElement | null>(null)
  useScrollIntoView(toggleMoreClinicsButtonRef.current, activeSlotRef.current)

  const variant = useCalendarVariant()

  const isNewPatient = patientType === PatientTypeOptions.NEW
  const allOrNonePractitionersAreSelected =
    preferredPractitionerReferenceIds.length === 0 || allPreferredPractitionersAreSelected

  const showMoreClinicsButton =
    hasMoreClinicsInCity && isNormalMode && isNewPatient && allOrNonePractitionersAreSelected

  const swipe = (swiper: SwiperClass): void => {
    const direction =
      swiper.swipeDirection === CALENDAR_DIRECTIONS.NEXT ? CALENDAR_DIRECTIONS.LEFT : CALENDAR_DIRECTIONS.RIGHT

    logUserInteraction(direction, CALENDAR_USER_ACTIONS.FINGER_SWIPE)

    swipeToPeriod(swiper.swipeDirection)
  }

  return (
    <Swiper onTouchEnd={swipe}>
      <SwiperSlide>
        {Object.entries(currentPeriod).map(([clinicId, availabilities], index) => (
          <React.Fragment key={clinicId}>
            {index !== 0 && !isTransitioning && <ClinicSlotsInfo clinicId={clinicId} />}
            <ClinicSlots
              activeSlotRef={activeSlotRef}
              clinicId={clinicId}
              currentPeriod={availabilities}
              selectedSlot={selectedBookableDateAndTime}
              variant={variant}
              isExpandable={index !== 0 && availabilities.some((day) => day.availabilities.length > 2)}
            />
            {cityClinicsLoading ? (
              <Spinner center />
            ) : (
              index === 0 &&
              showMoreClinicsButton &&
              !isTransitioning && <ToggleMoreClinicsButton toggleMoreClinicsButtonRef={toggleMoreClinicsButtonRef} />
            )}
          </React.Fragment>
        ))}
      </SwiperSlide>
    </Swiper>
  )
}
